'use client'

// React Imports
import { useState, useEffect } from 'react'

// Next Imports
import { useParams, useRouter } from 'next/navigation'

// MUI Imports
import useMediaQuery from '@mui/material/useMediaQuery'
import { styled, useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'

// Third-party Imports
import classnames from 'classnames'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

// Type Imports
import LoadingButton from '@mui/lab/LoadingButton'

import toast from 'react-hot-toast'

import type { SystemMode } from '@/core/types/types'
import type { Locale } from '@configs/i18n'

// Component Imports
import Link from '@/core/components/Link'
import Logo from '@/core/components/layout/shared/Logo'
import CustomTextField from '@/core/components/mui/TextField'

// Config Imports
import themeConfig from '@/core/configs/themeConfig'

// Hook Imports
import { useImageVariant } from '@/core/hooks/useImageVariant'
import { useSettings } from '@/core/hooks/useSettings'
import { useAuth } from '@core/contexts/authContext'
import { useRequireGuest } from '@core/hooks/useAuthGuard'

// Util Imports
import { getLocalizedUrl } from '@core/utils/i18n'

// i18n
import i18n, { t } from '@/core/i18n/setting'
import { useAPI } from '@/core/hooks/useAPI'
import { loginPasswordSchema } from '@/core/schema/auth-schema'

// import LanguageDropdown from '@/core/components/layout/shared/LanguageDropdown'

// Validation Schema Factory
const createLoginSchema = () =>
  z.object({
    email: z.string().email(t('emailInvalid')).min(1, t('emailRequired')),
    password: loginPasswordSchema()
  })

type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>

// Styled Custom Components
const LoginIllustration = styled('img')(({ theme }) => ({
  zIndex: 2,
  blockSize: 'auto',
  maxBlockSize: 680,
  maxInlineSize: '100%',
  margin: theme.spacing(12),
  [theme.breakpoints.down(1536)]: {
    maxBlockSize: 550
  },
  [theme.breakpoints.down('lg')]: {
    maxBlockSize: 450
  }
}))

const MaskImg = styled('img')({
  blockSize: 'auto',
  maxBlockSize: 355,
  inlineSize: '100%',
  position: 'absolute',
  insetBlockEnd: 0,
  zIndex: -1
})

const LoginV2 = ({ mode }: { mode: SystemMode }) => {
  // States
  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const [loginSchema, setLoginSchema] = useState(() => createLoginSchema())

  // Protect this page - redirect authenticated users
  useRequireGuest('/home')

  // React Hook Form
  const {
    control,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  })

  // Update schema when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setLoginSchema(createLoginSchema())
    }

    i18n.on('languageChanged', handleLanguageChange)

    return () => {
      i18n.off('languageChanged', handleLanguageChange)
    }
  }, [])

  // Vars
  const darkImg = '/images/pages/auth-mask-dark.png'
  const lightImg = '/images/pages/auth-mask-light.png'
  const darkIllustration = '/images/illustrations/auth/v2-login-dark.png'
  const lightIllustration = '/images/illustrations/auth/v2-login-light.png'
  const borderedDarkIllustration = '/images/illustrations/auth/v2-login-dark-border.png'
  const borderedLightIllustration = '/images/illustrations/auth/v2-login-light-border.png'

  // Hooks
  const { lang: locale } = useParams()
  const router = useRouter()
  const { settings } = useSettings()
  const theme = useTheme()
  const hidden = useMediaQuery(theme.breakpoints.down('md'))
  const authBackground = useImageVariant(mode, lightImg, darkImg)

  const { mutateAsync, isPending } = useAPI(['auth', 'login'], {
    skipGlobalErrorHandler: true
  })

  const { login } = useAuth()

  const characterIllustration = useImageVariant(
    mode,
    lightIllustration,
    darkIllustration,
    borderedLightIllustration,
    borderedDarkIllustration
  )

  const handleClickShowPassword = () => setIsPasswordShown(show => !show)

  // Form Submit Handler
  const onSubmit = async (data: LoginFormData) => {
    try {
      const response = await mutateAsync(data)
      const token = response.data.token

      // Check if token exists before proceeding
      if (token) {
        // Store token in cookie using auth context
        login(token, {
          id: Date.now().toString(),
          email: data.email
        })

        // Redirect to home page
        router.push('/home')
      } else {
        console.error('No token received from server')
      }
    } catch (error: any) {
      if (error.response.status === 401) {
        toast.error(t('invalidEmailOrPassword'))
      } else {
        toast.error(t('somethingWentWrong'))
      }

      console.error('Login error:', error)
    }
  }

  return (
    <div className='flex bs-full justify-center'>
      <div
        className={classnames(
          'flex bs-full items-center justify-center flex-1 min-bs-[100dvh] relative p-6 max-md:hidden',
          {
            'border-ie': settings.skin === 'bordered'
          }
        )}
      >
        <LoginIllustration src={characterIllustration} alt={t('characterImage')} />
        {!hidden && (
          <MaskImg
            alt={t('mask')}
            src={authBackground}
            className={classnames({ 'scale-x-[-1]': theme.direction === 'rtl' })}
          />
        )}
      </div>
      <div className='flex justify-center items-center bs-full bg-backgroundPaper !min-is-full p-6 md:!min-is-[unset] md:p-12 md:is-[480px]'>
        <Link
          href={getLocalizedUrl('/', locale as Locale)}
          className='absolute block-start-5 sm:block-start-[33px] inline-start-6 sm:inline-start-[38px]'
        >
          <Logo />
        </Link>
        <div className='flex flex-col gap-6 is-full sm:is-auto md:is-full sm:max-is-[400px] md:max-is-[unset] mbs-11 sm:mbs-14 md:mbs-0'>
          <div className='flex flex-col gap-1'>
            {/*<div className='absolute top-5 inline-end-5'>*/}
            {/*  <LanguageDropdown />*/}
            {/*</div>*/}
            <Typography variant='h4'>{`${t('welcomeMessage')} ${themeConfig.templateName}! ${t('welcomeEmoji')}`}</Typography>
            <Typography>{t('loginSubtitle')}</Typography>
          </div>
          <form noValidate autoComplete='off' onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-5'>
            <Controller
              name='email'
              control={control}
              render={({ field }) => (
                <CustomTextField
                  {...field}
                  autoFocus
                  fullWidth
                  label={t('emailOrUsername')}
                  placeholder={t('emailPlaceholder')}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              )}
            />
            <Controller
              name='password'
              control={control}
              render={({ field }) => (
                <div>
                  <CustomTextField
                    {...field}
                    fullWidth
                    label={t('password')}
                    placeholder={t('passwordPlaceholder')}
                    type={isPasswordShown ? 'text' : 'password'}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position='end'>
                            <IconButton
                              edge='end'
                              onClick={handleClickShowPassword}
                              onMouseDown={e => e.preventDefault()}
                            >
                              <i className={isPasswordShown ? 'tabler-eye-off' : 'tabler-eye'} />
                            </IconButton>
                          </InputAdornment>
                        )
                      }
                    }}
                  />
                </div>
              )}
            />

            <LoadingButton
              fullWidth
              loading={isPending}
              variant='contained'
              type='submit'
              disabled={!isValid || isPending}
            >
              {isPending ? t('loggingIn') : t('login')}
            </LoadingButton>
            <div className='flex justify-center items-center flex-wrap gap-2'>
              <Typography>{t('newUser')}</Typography>
              <Typography component={Link} color='primary.main' href={getLocalizedUrl('/register', locale as Locale)}>
                {t('createAccount')}
              </Typography>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default LoginV2
